/**
 * WinThemeRegistry — v3.18.0 (Strategy Stage, Phase 1)
 *
 * CRUD list for win themes on a pursuit. Auto-numbered T1, T2, T3...
 * Each theme can be flagged as a discriminator (sets us apart) or a
 * ghost (counterpunch against a competitor).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import {
  Plus, Trash2, ArrowUp, ArrowDown, Zap, Ghost, Save, X, Pencil, Layers,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WinThemeRegistry({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [themes, setThemes] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '', discriminator: false, ghost: false });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchThemes = useCallback(async () => {
    if (!pursuitId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/win-themes`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setThemes(body.themes || []);
    } catch (e) {
      console.error('win-themes fetch failed', e);
      setThemes([]);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchThemes(); }, [fetchThemes]);

  const handleCreate = async () => {
    if (!draft.title.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/win-themes`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(draft),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Win theme added');
      setDraft({ title: '', body: '', discriminator: false, ghost: false });
      setAdding(false);
      await fetchThemes();
    } catch (e) {
      console.error(e);
      toast.error('Failed to add theme');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditDraft({
      title: t.title,
      body: t.body || '',
      discriminator: !!t.discriminator,
      ghost: !!t.ghost,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/win-themes/${editingId}`,
        {
          method: 'PATCH',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(editDraft),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Theme updated');
      cancelEdit();
      await fetchThemes();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this win theme? Numbers will be re-compacted.')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/win-themes/${id}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Theme deleted');
      await fetchThemes();
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete');
    }
  };

  const handleMove = async (id, direction) => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/win-themes/${id}/move`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ direction }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchThemes();
    } catch (e) {
      console.error(e);
    }
  };

  if (themes === null) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <Card className="border-violet-100 shadow-sm" data-testid="win-theme-registry">
      <CardContent className="p-4 space-y-3">
        {themes.length === 0 && !adding && (
          <div
            className="text-center py-8 px-4 border-2 border-dashed border-violet-200 rounded-lg bg-violet-50/30"
            data-testid="win-themes-empty"
          >
            <Layers className="w-8 h-8 text-violet-400 mx-auto mb-2" />
            <p className="text-sm text-slate-700 font-medium">
              No win themes yet.
            </p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Capture the 3–5 messages you want every proposal volume to thread.
            </p>
            <Button
              onClick={() => setAdding(true)}
              data-testid="win-themes-add-first-btn"
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" /> Add first theme
            </Button>
          </div>
        )}

        {themes.map((t, idx) => (
          <div
            key={t.id}
            className={cn(
              'border rounded-lg p-3 transition-colors',
              t.discriminator ? 'border-amber-200 bg-amber-50/40' :
              t.ghost ? 'border-slate-300 bg-slate-50' :
              'border-slate-200 bg-white',
            )}
            data-testid={`win-theme-row-${idx}`}
          >
            {editingId === t.id ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-300 font-mono">
                    {t.code}
                  </Badge>
                  <Input
                    value={editDraft.title}
                    onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                    placeholder="Theme title…"
                    data-testid="win-theme-edit-title"
                  />
                </div>
                <Textarea
                  value={editDraft.body}
                  onChange={(e) => setEditDraft({ ...editDraft, body: e.target.value })}
                  placeholder="Body — describe the message and how each volume should carry it…"
                  rows={3}
                  data-testid="win-theme-edit-body"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editDraft.discriminator}
                      onChange={(e) => setEditDraft({ ...editDraft, discriminator: e.target.checked })}
                      data-testid="win-theme-edit-discriminator"
                    />
                    <Zap className="w-3.5 h-3.5 text-amber-600" /> Discriminator
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editDraft.ghost}
                      onChange={(e) => setEditDraft({ ...editDraft, ghost: e.target.checked })}
                      data-testid="win-theme-edit-ghost"
                    />
                    <Ghost className="w-3.5 h-3.5 text-slate-500" /> Ghost
                  </label>
                  <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving}>
                      <X className="w-3.5 h-3.5 mr-1" /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveEdit}
                      disabled={saving || !editDraft.title?.trim()}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                      data-testid="win-theme-save-edit-btn"
                    >
                      <Save className="w-3.5 h-3.5 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Badge
                  variant="outline"
                  className="bg-violet-100 text-violet-800 border-violet-300 font-mono mt-0.5"
                  data-testid={`win-theme-code-${idx}`}
                >
                  {t.code}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4
                      className="font-semibold text-slate-900 text-sm"
                      data-testid={`win-theme-title-${idx}`}
                    >
                      {t.title}
                    </h4>
                    {t.discriminator && (
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] gap-1">
                        <Zap className="w-3 h-3" /> Discriminator
                      </Badge>
                    )}
                    {t.ghost && (
                      <Badge className="bg-slate-200 text-slate-700 border border-slate-300 text-[10px] gap-1">
                        <Ghost className="w-3 h-3" /> Ghost
                      </Badge>
                    )}
                  </div>
                  {t.body && (
                    <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{t.body}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      disabled={idx === 0}
                      onClick={() => handleMove(t.id, 'up')}
                      title="Move up"
                      data-testid={`win-theme-move-up-${idx}`}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      disabled={idx === themes.length - 1}
                      onClick={() => handleMove(t.id, 'down')}
                      title="Move down"
                      data-testid={`win-theme-move-down-${idx}`}
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => startEdit(t)}
                      title="Edit"
                      data-testid={`win-theme-edit-${idx}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(t.id)}
                      title="Delete"
                      data-testid={`win-theme-delete-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {adding ? (
          <div
            className="border-2 border-dashed border-violet-300 rounded-lg p-3 space-y-2 bg-violet-50/30"
            data-testid="win-theme-new-form"
          >
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-300 font-mono">
                T{themes.length + 1}
              </Badge>
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Theme title (e.g., Cleared workforce on Day 1)"
                data-testid="win-theme-new-title"
                autoFocus
              />
            </div>
            <Textarea
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder="Body — what message do you want every volume to carry?"
              rows={3}
              data-testid="win-theme-new-body"
            />
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.discriminator}
                  onChange={(e) => setDraft({ ...draft, discriminator: e.target.checked })}
                  data-testid="win-theme-new-discriminator"
                />
                <Zap className="w-3.5 h-3.5 text-amber-600" /> Discriminator
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.ghost}
                  onChange={(e) => setDraft({ ...draft, ghost: e.target.checked })}
                  data-testid="win-theme-new-ghost"
                />
                <Ghost className="w-3.5 h-3.5 text-slate-500" /> Ghost
              </label>
              <div className="ml-auto flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setAdding(false); setDraft({ title: '', body: '', discriminator: false, ghost: false }); }}
                  disabled={saving}
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={saving || !draft.title.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="win-theme-save-new-btn"
                >
                  <Save className="w-3.5 h-3.5 mr-1" /> Save theme
                </Button>
              </div>
            </div>
          </div>
        ) : themes.length > 0 ? (
          <Button
            variant="outline"
            onClick={() => setAdding(true)}
            className="w-full border-dashed border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="win-theme-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add another theme
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
